import { useCallback, memo } from 'react';
import { useSidebar } from '@/context/SidebarContext';
import { Sidebar } from '@/components/shared/Sidebar';
import { InputPanel } from '@/components/Forms';
import { SummaryPanel } from '@/components/Summary';
import { Container } from '@/components/Layout/Container';
import { PrimaryHeader } from '@/components/Layout/Header/PrimaryHeader';
import { SecondaryHeader } from '@/components/Layout/Header/SecondaryHeader';
import { useProject } from '@/context/ProjectContext';
import { saveCVRCalculation } from '@/lib/supabase/cvr';
import type { SavedCalculation } from '@/types/cvr';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

// Memoize static components to prevent unnecessary re-renders
const MemoizedPrimaryHeader = memo(PrimaryHeader);
const MemoizedSecondaryHeader = memo(SecondaryHeader);

export function CVRTool() {
  const { isCollapsed } = useSidebar(); // Get sidebar state
  const { state, dispatch } = useProject();
  const { toast } = useToast();

  const handleSave = useCallback(
    async (name: string, data: unknown) => {
      try {
        if (!name?.trim()) {
          throw new Error('Calculation name is required');
        }

        if (!data || typeof data !== 'object') {
          throw new Error('Invalid calculation data');
        }

        await saveCVRCalculation(name, data);
        toast({
          title: 'Success',
          description: 'Calculation saved successfully',
        });
      } catch (error) {
        toast({
          title: 'Error',
          description:
            error instanceof Error ? error.message : 'Failed to save calculation',
          variant: 'destructive',
        });
      }
    },
    [toast]
  );

  const handleLoad = useCallback(
    (calculation: SavedCalculation) => {
      try {
        if (!calculation?.data) {
          throw new Error('Invalid calculation data');
        }

        dispatch({
          type: 'UPDATE_DATA',
          payload: calculation.data,
        });
        toast({
          title: 'Success',
          description: 'Calculation loaded successfully',
        });
      } catch (error) {
        toast({
          title: 'Error',
          description:
            error instanceof Error ? error.message : 'Failed to load calculation',
          variant: 'destructive',
        });
      }
    },
    [dispatch, toast]
  );

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <MemoizedPrimaryHeader className="px-4 sm:px-6" />
      <MemoizedSecondaryHeader className="px-4 sm:px-6" />

      <div className="flex flex-1 flex-col md:flex-row">
        <Sidebar
          toolType="cvr_calculations"
          data={state.data}
          onSave={handleSave}
          onLoad={handleLoad}
          className={cn(
            'w-full overflow-y-auto border-b transition-all duration-300',
            'md:border-r md:border-b-0',
            isCollapsed ? 'md:w-20' : 'md:w-80'
          )}
          aria-label="CVR calculation sidebar"
          role="complementary"
        />

        <MainContent isCollapsed={isCollapsed} />
      </div>
    </div>
  );
}

const MainContent = memo(({ isCollapsed }: { isCollapsed: boolean }) => (
  <main
    className={cn(
      'flex-1 overflow-y-auto transition-all duration-300',
      isCollapsed ? 'md:ml-20' : 'md:ml-80'
    )}
    role="main"
  >
    <Container className="px-4 sm:px-6">
      <section className="space-y-4 sm:space-y-8 py-4 sm:py-6">
        <ContentSection title="Input Data">
          <InputPanel className="mt-2 sm:mt-4" />
        </ContentSection>

        <ContentSection title="Summary">
          <SummaryPanel className="mt-2 sm:mt-4" />
        </ContentSection>
      </section>
    </Container>
  </main>
));

const ContentSection = memo(
  ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div className="border-b pb-4 last:border-b-0 last:pb-0 last:pt-4">
      <h2 className="text-base sm:text-lg font-medium mb-2 sm:mb-4">{title}</h2>
      {children}
    </div>
  )
);
